'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import type { PropsWithChildren } from 'react';
import { useEffect, useRef } from 'react';
import { AuthStatusEnum, useAuth } from '@sola/auth-sdk/store.tsx';
import { isDevelopmentEnvironment } from '@sola/shared-utils/misc.ts';
import { DataPrimer } from './primer';
const ReactQueryDevtools = dynamic(() => import('@tanstack/react-query-devtools').then(mod => ({
  default: mod.ReactQueryDevtools
})), {
  ssr: false
});
export type SupporterProviderProps = PropsWithChildren & {
  includeDevTools?: boolean;
  /**
   * When true, a full page reload is triggered once per session the first
   * time the auth store transitions from authenticated into a logged-out
   * state. The store's `logOut` action moves through
   * `Authenticated → InProgress → LoggedOut`; we latch on first
   * `Authenticated` and fire when we later observe `LoggedOut`, so the
   * intermediate `InProgress` (or any transient error state) just falls
   * through. Use on layouts that render authenticated-only content (e.g. the
   * My app) so server components and client caches are rebuilt against the
   * logged-out session. Defaults to false to keep behavior unchanged for
   * layouts that handle logout differently (checkout, skippy, storybook).
   */
  refreshOnLogout?: boolean;
};
export const SupporterProvider: React.FC<SupporterProviderProps> = ({
  children,
  includeDevTools = true,
  refreshOnLogout = false
}) => {
  const {
    currentStatus
  } = useAuth('currentStatus');
  const wasAuthenticatedRef = useRef(false);
  // One-shot latch: even if `location.reload()` is no-op'd (e.g. a
  // `beforeunload` handler cancels navigation) we still treat the reload as
  // attempted and don't keep retrying on subsequent renders.
  const hasReloadedRef = useRef(false);
  useEffect(() => {
    if (currentStatus === AuthStatusEnum.Authenticated) {
      wasAuthenticatedRef.current = true;
      return;
    }
    if (!refreshOnLogout || hasReloadedRef.current) {
      return;
    }
    if (wasAuthenticatedRef.current && currentStatus === AuthStatusEnum.LoggedOut) {
      hasReloadedRef.current = true;
      globalThis.location.reload();
    }
  }, [currentStatus, refreshOnLogout]);
  const queryClientRef = useRef<QueryClient | null>(null);
  if (!queryClientRef.current) {
    queryClientRef.current = new QueryClient({
      defaultOptions: {
        queries: {
          // Data will be considered fresh forever (until you invalidate/update it manually)
          staleTime: Infinity,
          retry: 1
        }
      }
    });
  }
  if (isDevelopmentEnvironment) {
    console.log('Rendering provider');
  }
  return <QueryClientProvider client={queryClientRef.current}>
      <DataPrimer />
      {children}
      {includeDevTools && isDevelopmentEnvironment && <ReactQueryDevtools />}
    </QueryClientProvider>;
};